import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, useTheme, useMediaQuery, Alert, CircularProgress, Button, Paper, Stack, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import SearchAndFilterBar from '../../components/Common/SearchAndFilterBar';
import SubjectForm from '../../components/Forms/SubjectForm';
import { subjectService, classService, userService, roleService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmationDialog from '../../components/Common/ConfirmationDialog';

const Subjects = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };
  const drawerWidth = 280;
  const { isTeacher, isAdmin, user } = useAuth();
  const isTeacherUser = isTeacher() && !isAdmin();

  const [subjects, setSubjects] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    subject: null,
  });

  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const fetchRefs = useCallback(async () => {
    const [clsRes, rolesRes, usersRes] = await Promise.all([
      classService.getAll(),
      roleService.getAll(),
      userService.getAll(),
    ]);
    const teacherRole = (rolesRes.data || rolesRes)?.find(r => r.role_name?.toLowerCase() === 'teacher');
    const allUsers = usersRes.data || usersRes;
    const teacherUsers = teacherRole ? (allUsers || []).filter(u => u.role_id === teacherRole.id) : [];
    setClasses(clsRes.data || clsRes || []);
    setTeachers(teacherUsers);
  }, []);

  const fetchSubjects = useCallback(async (params) => {
    try {
      setLoading(true);
      setError(null);
      
      // For teachers, automatically filter by their teacher_id
      const finalParams = { ...params };
      if (isTeacherUser && user) {
        const userId = user?.id || user?.user_id;
        if (userId) {
          finalParams.teacher_id = userId;
        }
      }
      
      const res = await subjectService.getAll(finalParams);
      const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setSubjects(arr);
      setFiltered(arr);
    } catch (err) {
      setError(err.message || 'Failed to load subjects');
    } finally {
      setLoading(false);
    }
  }, [isTeacherUser, user]);

  useEffect(() => {
    fetchRefs();
    fetchSubjects();
  }, [fetchRefs, fetchSubjects]);

  const [currentSearch, setCurrentSearch] = useState('');
  const [currentFilters, setCurrentFilters] = useState({});

  const applyServerFilters = (search, filters) => {
    const params = {};
    if (search && search.trim()) params.q = search.trim();
    if (filters?.class_id) params.class_id = filters.class_id;
    // For admins, allow filtering by teacher_id
    // For teachers, teacher_id is automatically set in fetchSubjects
    if (!isTeacherUser && filters?.teacher_id) {
      params.teacher_id = filters.teacher_id;
    }
    fetchSubjects(params);
  };

  const handleSearch = (term) => {
    setCurrentSearch(term);
    applyServerFilters(term, currentFilters);
  };

  const handleFilter = (filters) => {
    setCurrentFilters(filters);
    applyServerFilters(currentSearch, filters);
  };

  const handleClear = () => {
    setCurrentSearch('');
    setCurrentFilters({});
    fetchSubjects();
  };

  const handleViewClass = (row) => {
    if (row.class_id) {
      navigate(`/subjects/class/${row.class_id}`);
    }
  };

  const getClassLabel = (row) => {
    const apiName = row.class_name;
    if (apiName) {
      const section = row.class_section;
      const className = apiName.startsWith('Class ') ? apiName : `Class ${apiName}`;
      return section ? `${className} - ${section}` : className;
    }
    const classItem = classes.find((c) => c.id === row.class_id);
    if (!classItem) return '—';
    const section = classItem.section;
    return section ? `${classItem.name} - ${section}` : classItem.name;
  };

  const handleAdd = () => { setEditingSubject(null); setModalOpen(true); };
  const handleEdit = (row) => { setEditingSubject(row); setModalOpen(true); };

  const handleDeleteClick = (row) => {
    setConfirmDialog({
      open: true,
      subject: row,
    });
  };

  const handleDeleteConfirm = async () => {
    const subject = confirmDialog.subject;
    if (!subject) return;

    try {
      setLoading(true);
      await subjectService.delete(subject.id);
      await fetchSubjects();
      setConfirmDialog({ open: false, subject: null });
    } catch (err) {
      setError(err.message || 'Failed to delete subject');
      setConfirmDialog({ open: false, subject: null });
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => { setModalOpen(false); setEditingSubject(null); };

  const handleModalSave = useCallback(async (formData) => {
    try {
      setModalLoading(true);
      if (editingSubject) {
        await subjectService.update(editingSubject.id, formData);
      } else {
        await subjectService.create(formData);
      }
      await fetchSubjects();
      handleModalClose();
    } catch (err) {
      setError(err.message || 'Failed to save subject');
    } finally {
      setModalLoading(false);
    }
  }, [editingSubject, fetchSubjects]);

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            // On mobile: always full width, no margin (sidebar overlays)
            // On desktop: adjust width and margin based on sidebar state
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
            <SearchAndFilterBar
              title={isTeacherUser ? "My Subjects & Class" : "Subjects"}
              onSearch={handleSearch}
              onFilter={handleFilter}
              onCreate={!isTeacherUser ? handleAdd : undefined}
              onClear={handleClear}
              searchPlaceholder="Search subjects by name..."
              searchValue={currentSearch}
              filterValue={currentFilters}
              filterOptions={
                isTeacherUser
                  ? [
                      // Teachers can only filter by class
                      { field: 'class_id', label: 'Class', options: (classes || []).map(c => ({ value: c.id, label: c.section ? `${c.name} - ${c.section}` : c.name })) },
                    ]
                  : [
                      // Admins can filter by class and teacher
                      { field: 'class_id', label: 'Class', options: (classes || []).map(c => ({ value: c.id, label: c.section ? `${c.name} - ${c.section}` : c.name })) },
                      { field: 'teacher_id', label: 'Teacher', options: (teachers || []).map(t => ({ value: t.id, label: t.full_name })) },
                    ]
              }
              createButtonText="Add Subject"
            />
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: { xs: 150, sm: 200 } }}>
                <CircularProgress />
              </Box>
            ) : filtered.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{ p: 3, borderRadius: 3, textAlign: 'center', borderColor: 'divider', mt: 2 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {isTeacherUser ? 'No subjects assigned to you.' : 'No subjects found.'}
                </Typography>
              </Paper>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gap: { xs: 1.5, sm: 2 },
                  gridTemplateColumns: '1fr',
                  mt: 2,
                }}
              >
                {filtered.map((subject, index) => (
                  <Paper
                    key={subject.id}
                    variant="outlined"
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: 2,
                      borderColor: 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between" flexWrap="nowrap">
                      {/* Left: Icon + Details */}
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                        <MenuBookRoundedIcon sx={{ width: 48, height: 48, color: 'primary.main', flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{ mb: 0.5 }}
                          >
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                              {subject.name || 'Untitled Subject'}
                            </Typography>
                            <Chip
                              size="small"
                              color="primary"
                              variant="outlined"
                              label={getClassLabel(subject)}
                            />
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <Typography variant="body2" fontWeight={600}>
                              Teacher:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {subject.teacher_name || 'Not assigned'}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>

                      {/* Right: Actions */}
                      <Stack 
                        direction="row" 
                        spacing={1} 
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{ 
                          flex: '0 0 auto', 
                          minWidth: 'auto',
                          flexWrap: 'wrap'
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewClass(subject)}
                          sx={{ textTransform: 'none' }}
                        >
                          View Class
                        </Button>
                        {!isTeacherUser && (
                          <>
                            <Button 
                              size="small" 
                              variant="outlined"
                              onClick={() => handleEdit(subject)} 
                              startIcon={<EditIcon />} 
                              sx={{ textTransform: 'none' }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => handleDeleteClick(subject)}
                              startIcon={<DeleteIcon />}
                              sx={{ textTransform: 'none' }}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <SubjectForm
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSave}
        initialData={editingSubject}
        loading={modalLoading}
        classes={classes}
        teachers={teachers}
        title={editingSubject ? 'Edit Subject' : 'Add New Subject'}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, subject: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Subject"
        message={confirmDialog.subject ? `Are you sure you want to delete "${confirmDialog.subject.name}"? This action cannot be undone.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        severity="error"
      />
    </>
  );
};

export default Subjects;

